import { useState } from 'react'
import { Bug } from 'lucide-react'
import { cn } from '../../lib/shadcn/utils'
import { useCapabilities } from '../../hooks/useCapabilities'
import { ReportProblemDialog } from './ReportProblemDialog'

/**
 * Report a problem, from wherever you happen to be standing.
 *
 * IT LIVES IN THE SIDEBAR FOR THE SAME REASON RequestDesignButton DOES, and the
 * reason is stronger here. A broken screen is reported at the moment somebody is
 * looking at it, and the one fact that makes a bug report useful, which screen it
 * was, is the fact that goes stale first. A form you have to navigate to loses
 * that fact along with the report: by the time you have found the page you are no
 * longer standing where it happened.
 *
 * The dialog records the current path automatically, which only works because the
 * button is reachable from the page where the problem is.
 *
 * Gated on canView, the same gate the report itself uses. Reporting that
 * something is broken is not a privilege, and a queue only two people may write
 * to is a queue everybody goes around, after which the problem is unrecorded
 * rather than unreported.
 */
export function ReportProblemButton({ compact = false }: { compact?: boolean }) {
  const { canView } = useCapabilities()
  const [open, setOpen] = useState(false)

  if (!canView) return null

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={compact ? 'Report a problem' : undefined}
        aria-label="Report a problem"
        className={cn(
          'flex w-full items-center gap-3 rounded-md border border-dashed border-[hsl(var(--brand))]/50 px-3 py-2.5 text-sm font-medium text-[hsl(var(--brand))] transition-colors hover:bg-[hsl(var(--brand-soft))] sm:py-2',
          compact && 'justify-center px-2',
        )}
      >
        <Bug className="h-4 w-4 shrink-0" />
        {!compact && 'Report a problem'}
      </button>
      <ReportProblemDialog open={open} onOpenChange={setOpen} />
    </>
  )
}
